import $ from '@common/Scripts/libs/jquery-1.12.4.min.js';
import cookies from '@common/Scripts/libs/js.cookie.js';
import pageLoaders from '@app/runtime/pages-loaders.js';
import url from '@common/Scripts/core/url/url.js';

/*
 * We expect that $ contains .type and .extended, other functions of jquery is not required.
 * So in node we need implements only this two functions. Else in loading objects we use
 * $.ajax but in node we don't need this
 */

const nodejs = typeof window === 'undefined',
	runtimeGlobal = typeof globalThis !== 'undefined'
		? globalThis
		: nodejs ? global : window,
	getRequestPath = function () {
		// On the server `path` is backed by AsyncLocalStorage. It must be read
		// for every request rather than captured while this module is evaluated.
		return runtimeGlobal.path || '';
	},
	objectKeys = Object.keys,
	arrayPrototype = Array.prototype,
	deepExtend = function (destination, source) {
		const keys = objectKeys(source),
			l = keys.length;
		let i;
		for (i = 0; i < l; ++i) {
			const property = keys[i],
				value = source[property];

			if (value && value.constructor && value.constructor === Object) {
				destination[property] = destination[property] || {};
				if (property !== 'parent') {
					deepExtend(destination[property], value);
				}
			} else {
				destination[property] = value;
			}
		}

		return destination;
	},
	fastExtend = function (destination, source) {
		const keys = objectKeys(source),
			l = keys.length;
		let i;
		for (i = 0; i < l; ++i) {
			destination[keys[i]] = source[keys[i]];
		}
		return destination;
	};

if (nodejs) {
	/* implementation without jsviews */
	fastExtend(arrayPrototype, {
		refresh: function (elements) {
			if (elements) {
				const self = this;
				self.splice.apply(self, [0, self.length].concat(elements));
			}
			return this;
		},
		insertRange: function (i, item) {
			arrayPrototype.splice.apply(this, [i, 0].concat(item));
			return this;
		},
		removeRange: function (i, count) {
			this.splice(i, count);
			return this;
		}
	});
} else {
	/* implementation with jsviews */
	fastExtend(arrayPrototype, {
		refresh: function (r) {
			let changed = false,
				self = this;

			if (self._ && !self._.ignoreObserving) {
				changed = true;
				self._.ignoreObserving = true;
			}
			$.observable(self).refresh(r || self);
			if (changed) {
				self._.ignoreObserving = false;
			}
			return self;
		},
		insertRange: function (i, item, isWaitedLoad) {
			const array = this;
			const observeSend = function () {
				if (array.get(item.id)) {
					$.observable(array).insert(i, item);
				}
				$.unobserve(array._, 'send', observeSend);
			};

			if (isWaitedLoad && array._ && array._.send && typeof item.id !== 'undefined') {
				$.observe(array._, 'send', observeSend);
			} else {
				$.observable(array).insert(i, item);
			}
			return this;
		},
		removeRange: function (i, count) {
			$.observable(this).remove(i, count);
			return this;
		},
		_removeRange: function (i, count) {
			this.splice(i, count);
			return this;
		}
	});
}

let f = {
	// key - object type name
	// value - default values for object
	objects: {},
	// key - object type name
	// value - proto methods
	protos: {},
	stubs: {},
	// this property set in prototype of object and use in object request and handling notifications
	// key - object type name
	// value - alternative object type name
	// client to server
	cts: {},
	// server to client
	stc: {},
	// whether to set root link for objects
	// SSR object collections must always belong to the request root. Do not
	// depend on an entry point mutating a global before this ESM is evaluated.
	setParents: nodejs || Boolean(runtimeGlobal.factorySetParents),
	// collections of objects
	collections: {
		/**
		 * get object or object[] from the $.collections[type]
		 * @param {string} type - type of object in collection
		 * @param {string} property - object property for matching
		 * @param {string} value - value to match
		 * @param {boolean} all - flag
		 * @returns {(?object|?object[])} - if 'all' flag is false then returns the first entry or null,
		 * else returns all the entries in the array or an empty array
		 */
		xpath: function (type, property, value, all) {
			let r = all ? [] : null;
			const items = this,
				collection = items[type];

			if (typeof collection === 'undefined' || collection === null)
				return r;

			objectKeys(items[type]).every(function (item) {
				const t = items[type][item];
				if (f.xpath(t, property) === value) {
					if (all) {
						r.push(t);
					} else {
						r = t;
						return false;
					}
				}
				return true;
			});

			return r;
		},
		/**
		 * filter all items in collections
		 * @param {function} f - filter function
		 * @returns {object[]} - result array
		 */
		filter: function (f) {
			return objectKeys(this)
				.map(function (type) {
					return objectKeys($.collections[type])
						.map(function (id) {
							return $.collections[type][id];
						});
				})
				.map(function (itemsOfType) {
					return itemsOfType.filter(f);
				})
				.reduce(function (result, itemsOfNextType) {
					return result.concat(itemsOfNextType);
				}, []);
		}
	},
	// start client id. Creates a random integer between -1000 and -10.000.000 divisible by 1000
	id: Math.floor(Math.random() * 10000) - 10000 * 1000,
	/** @method
	 * return new unique client id
	 * @returns {number} - client id
	 */
	getId: function () {
		return --this.id;
	},
	/** @method
	 * set property of object
	 * @param {object} obj - object for setting property
	 * @param {object|string} key - key of property or dictionary with property|value
	 * @param {object} [value] - value for setting property if key is a string
	 * @returns {object} - factory object for support chaining
	 */
	setProperty: function (obj, key, value) {
		const type = $.type(key);
		if (type === 'string') {
			obj[key] = value;
		} else if (type === 'object') {
			fastExtend(obj, key);
		} else {
			throw new Error('Invalid key type. It should be string or object');
		}
		return this;
	},
	/** @method
	 * get value of object property by xpath path
	 * @param {object} node
	 * @param {string} path
	 * @returns {object} value ob object property
	 */
	xpath: function (node, path) {
		let r = node;
		const arr = path.split('.');

		arr.forEach(function (item) {
			if (item.indexOf('[') !== -1) {
				const t = item.split('[');
				r = r[t[0]][t[1].split(']')[0]];
			} else {
				r = r[item];
			}
		});
		return r;
	},
	/** @method
	 * get object of type type by obj
	 * @param {object|string} obj
	 * @param {string} type
	 * @param {object} parent
	 * @param {function()} callback
	 * @returns {object}
	 */
	getObject: function (obj, type, parent, callback, ignoreLoad) {
		const reload = obj._reload;

		if ($.type(obj) === 'string') {
			type = obj;
			obj = {};
		}

		let r;
		const t = f.protos[type];

		if (t && t.unique) {
			t.type = type;
			r = t.unique(obj);
			if (r == null) {
				r = this.create(obj, type, parent);
			}
		} else {
			r = this.create(obj, type, parent);
		}

		if (r) {
			// if is dummy object for page we need to call callback too
			if (r.id > 0 && !reload || !r.load) {
				callback && callback(r);
			} else {
				if (!nodejs) {
					// #todo (important for init and pages) maybe this should done in custom load function

					const location = window.location;

					if (!location.origin) {
						location.origin = location.protocol + '//' + location.hostname + (location.port ? ':' + location.port : '');
					}

					r._.params.fullpath = r.realpath ? location.origin + '/' + r.realpath + url.serializeStateSearch() : location.href;

					r._.params.clientId = r.clientId;

					if (ignoreLoad) {
						callback && callback(r);
					} else {
						r.load(function () {
							callback && callback(r);
						});
					}
				}
			}
		}

		return r;
	},
	/** @method
	 * create new type so we can call create with it
	 * @example
	 * factory.add('typeName');
	 * @example
	 * factory.add('typeName', { property: 1 });
	 * @example
	 * factory.add('typeName', { property: 1 }, 'object');
	 * @param {...(object|string)} - first argument must be a string - name of type
	 * second argument is an object and contains default properties for object
	 * all next arguments is a string or an object. if it is string, then it
	 * take default properies and prototype of type with such name. if it is object, then it extend created type prototype with it
	 * to new type
	 */
	add: function () {
		const self = this,
			a = arrayPrototype.slice.call(arguments, 0);

		if (a.length <= 0) {
			console.log('can\'t create type without name');
			return;
		}

		const t = a[0];
		if ($.type(t) !== 'string') {
			console.log('type name should be string');
			return;
		}

		self.objects[t] = self.objects[t] || {};
		self.protos[t] = self.protos[t] || {};

		const obj = a[1];

		for (var i = 2; i < a.length; i++) {
			const arg = a[i];
			if ($.type(arg) === 'string') {
				if (arg !== self.stubs[t]) {
					deepExtend(self.objects[t], self.objects[arg]);
					deepExtend(self.protos[t], self.protos[arg]);
				}
			} else {
				deepExtend(self.protos[t], arg);

				if (arg.cts) {
					$.extend(self.cts, arg.cts);
				}

				// stc object must contain name of type on server with lowered case of characters!
				// stc: { nameoftypeonserverwithloweredcase: nameOfTypeOnClient }
				if (arg.stc) {
					$.extend(self.stc, arg.stc);
				}
			}
		}

		if (obj) {
			deepExtend(self.objects[t], obj);
		}

		// RequireJS used to provide this metadata through its private registry.
		// ESM dependencies are discovered by the build, while modules that need
		// runtime SSR metadata can declare `dependencies` on their prototype.
		const dependenciesCopy = (self.protos[t].dependencies || []).concat();
		for (var i = 0; i < dependenciesCopy.length; ++i) {
			for (let j = i + 1; j < dependenciesCopy.length; ++j) {
				if (dependenciesCopy[i] === dependenciesCopy[j])
					dependenciesCopy.splice(j--, 1);
			}
		}

		self.protos[t].dependencies = dependenciesCopy;

		self.collections[t] = self.collections[t] || {};

		self.stubs[t] = undefined;
	},
	/** @method
	 * whether a stub of type exists
	 * @param {string} type
	 * @returns {boolean}
	 */
	hasStub: function (type) {
		return this.stubs[type];
	},
	/** @method
	 * add a stub for an object type to be extended laiter when js file is loaded
	 * @param {string} type - new type name
	 * @param {string} stub - name of the type to make stub from
	 */
	addStub: function (type, stub) {
		const self = this;
		self.add(type, {}, stub);
		self.stubs[type] = stub;
	},
	/** @method
	 * get root object or undefined if object contains no root link
	 * @param {object} obj - any object containing root link
	 * @returns {object}
	 */
	getRoot: function (obj) {
		return $.setParents
			? obj && obj._ && obj._.parent
			: $.site;
	},
	/** @method
	 * Get collections contained in root object if $.setParents flag is true
	 * or get $.collections if $.setParents flag is false.
	 * @param {object} obj - any object
	 * @param {string} [type] - if specified then collection for specified type is returned
	 * @returns {object} - all collections or collection of specified type
	 */
	getCollections: function (obj, type) {
		const collections = f.setParents && nodejs
			? obj && obj._ && obj._.parent && obj._.parent._.collections
			: f.collections;

		if (!collections) {
			return undefined;
		} else if (!type) {
			return collections;
		} else {
			if (!collections[type]) {
				collections[type] = {};
			}

			return collections[type];
		}
	},
	/** @method
	 * Get object of type with id from collections
	 * @param {object} obj - any object
	 * @param {string} type - type of an object to search for
	 * @param {number} id - id of an object to search for
	 * @returns {object}
	 */
	cget: function (obj, type, id) {
		const f = this;
		return f.getCollections(obj, type)[id];
	},
	/** @method
	 * Get object ar objects wich property equals to value from collections
	 * @param {object} obj - any object
	 * @param {string} type - type of an object to search for
	 * @param {string} property - name of the property
	 * @param {string|number|object|boolean} value - value of the property
	 * @param {boolean} all - if true returns array of found objects, else returns first found object
	 * @returns {object|object[]} - object or array of objects
	 */
	cxpath: function (obj, type, property, value, all) {
		const f = this;
		return f.getCollections(obj).xpath(type, property, value, all);
	},
	/** @method
	 * filter all items in collections
	 * @param {object} obj - any object
	 * @param {function} func - filter function
	 * @returns {object|object[]} - object or array of objects
	 */
	cfilter: function (obj, func) {
		const f = this;
		return f.getCollections(obj).filter(func);
	},
	/** @method
	 * Whether the object is initialized or created using factory.
	 * @param {object} obj
	 * returns {boolean}
	 */
	isInitialized: function (obj) {
		return obj && obj._ && obj._.initialized;
	},
	/** @method
	 * initialize array
	 * @param {object[]} self - destination array
	 * @param {object[] | listObject} r - source array or list object { list: [item1, item2], count: totalElementsOnServer, offset: offsetOfArrayInListProperty, _ : _ExtensionsObject }
	 * @param {boolean} push - if true push all items, otherwise reinit items with matching ids and push everything else
	 * @param {} obj - object owner of source
	 *
	 * desc object listObject
	 *		@prop _ {object} - _ extensions
	 *		@prop list {object[]} - array of items
	 *		@prop count {number} - total items on server
	 *		@prop offset {number} - offset of array in list-property
	 */
	set: function (self, r, push, obj) {
		const f = this;
		let i;

		if (self === r) {
			return;
		}

		if (r) {
			if (self._ && self._.type) {
				if (!(r instanceof Array) && r.count !== undefined) {
					// Initializing array from list object

					if (r._) {
						deepExtend(self._, r._);
						if (r._.parent) {
							self._.parent = r._.parent;
						}
					}

					f.setProperty(self._, 'rows', r.count);

					if (r.offset && !self._.autofill && (!self._.loaded || self._.rows === undefined)) {
						f.setProperty(self._, 'offset', r.offset);
					}

					if (self.length === 0 && self._.autofill) {
						self._push(Array(self._.rows));
						f.setProperty(self._, 'offset', (r.offset || 0) - self._.rows);
					}

					self.setObserve();

					r = r.list;
				} else if (r._ && !self._.autofill) {
					// initializing array from another initialized array (autofill is false)

					if (r._.rows && self._.rows === undefined) {
						self._.rows = r._.rows;
					}

					// todo check it, I think we can use offset to set start offset of array, and shouldn't change it after that anymore!
					if (r._.offset && !self._.offset) {
						self._.offset = r._.offset;
					}
				}

				if (r != null && self._.rewriteOld) {
					if (r.length > 0) {
						const newItems = [];

						for (let p = 0; p < r.length; p++) {
							var item = r[p];
							if (item) {
								var tmpType = self._.type === 'auto' ? item._type.toLowerCase() : self._.type,
									targetType = $.stc[tmpType] || tmpType;

								if ($.isInitialized(item)) {
									newItems.push(item);
								} else {
									if ($.setParents) {
										if (item._) {
											item._.parent = self._.parent;
										} else {
											item._ = { parent: self._.parent };
										}
									}

									newItems.push(f.create(item, targetType, item._ && item._.parent));
								}
							} else {
								newItems.push(undefined);
							}
						}

						self.refresh([]);
						self._push(newItems);
					} else {
						self.refresh([]);
					}
				} else if (r && r.length > 0) {
					let initedItems = [];

					if (!push && self.length > 0 && !self._.negativeCount && !self._.autofill) {
						// find elements which we don't need to insert into array

						initedItems = new Array(r.length);
						for (i = 0; i < r.length; i++) {
							if (r[i].id || r[i].clientId) {
								var item = null;

								if (r[i].id > 0) {
									item = self.get(r[i].id);
								}

								if (!item) {
									item = self.get('clientId', r[i].clientId);
								}

								if (item) {
									if (item.init && !$.isInitialized(r[i])) {
										item.init(r[i]);
									}
									initedItems[i] = true;
								}
							} else {
								if (self.length > i) {
									f.init(self[i], r[i]);
									initedItems[i] = true;
								}
							}
						}
					}

					const itemsToInsert = [];
					for (let j = 0; j < r.length; j++) {
						// prepare elements to be inserted into array
						var item = r[j];

						if (!initedItems[j]) {
							if (item) {
								var tmpType = self._.type === 'auto' ? item._type.toLowerCase() : self._.type,
									// if self._.type of array isn't 'auto', still $.stc will be applied
									targetType = $.stc[tmpType] || tmpType;

								if ($.isInitialized(item)) {
									itemsToInsert.push(item);
								} else {
									if ($.setParents) {
										if (item._) {
											item._.parent = self._.parent;
										} else {
											item._ = { parent: self._.parent };
										}
									}

									itemsToInsert.push(f.create(item, targetType, item._ && item._.parent));
								}
							} else {
								itemsToInsert.push(undefined);
							}
						}
					}

					// insert new elements into array
					if (self._.negativeCount) {
						self._.negativeCount = false;
						self._.offset -= itemsToInsert.length;
						self.insert(0, itemsToInsert);
					} else if (self._.insertToStart) {
						self.insert(0, itemsToInsert.reverse());
					} else {
						if (self._.autofill) {
							self.removeRange(self._.offset + self._.rows, itemsToInsert.length);
							self.insertRange(self._.offset + self._.rows, itemsToInsert);
						} else {
							if (!self._.clearBeforeReload) {
								// this is jsviews bug fix kostyl
								self.refresh([]);
								// this is jsviews bug fix kostyl
								self.refresh(itemsToInsert);
							} else {
								if (push || itemsToInsert.length != 0) {
									self._.ignoreObserving = true;
									self._push(itemsToInsert);
									self._.ignoreObserving = false;
								}
							}
						}
					}
				} else {
					if (!self._.clearBeforeReload) {
						self.refresh([]);
					}
				}
			} else {
				if (self.length === r.length) {
					const l = r.length;
					if (self.length > 0) {
						if (self[0] instanceof Object) {
							for (i = 0; i < l; i++) {
								f.init(self[i], r[i]);
							}
						} else {
							self.refresh(r);
						}
					}
				} else {
					self._push(r);
				}
			}
		}
	},
	/** @method
	 * initialize array with langs flag
	 * @param {object[]} oldArray - array to be initialized
	 * @param {object[]} newArray - array with new values
	 */
	initLangsArray: function (oldArray, newArray) {
		for (let i = 0; i < newArray.length; i++) {
			const newLangItem = newArray[i];
			const existingLangItem = oldArray.get('lang', newLangItem.lang);
			if (existingLangItem) {
				f.init(existingLangItem, newLangItem);
			} else {
				oldArray.push(newLangItem);
			}
		}
	},
	/** @method
	 * initialize object self with values from obj
	 * @param {object} self
	 * @param {object} obj
	 * @returns {object} - initialized object
	 */
	init: function (self, obj) {
		const f = this;

		if (self === obj) {
			return self;
		}

		if (obj) {
			if (obj._) {
				// prevent collections clonning
				let tempCollections;
				if (obj._ && obj._.collections) {
					tempCollections = obj._.collections;
					obj._.collections = undefined;
				}

				deepExtend(self._, obj._);

				if (obj._.parent) {
					self._.parent = obj._.parent;
				}

				if (tempCollections) {
					obj._.collections = tempCollections;
				}
			}

			const selfKeys = objectKeys(self);
			if (selfKeys.length > 0) {
				for (let i = 0; i < selfKeys.length; i++) {
					const propName = selfKeys[i],
						oldValue = self[propName],
						newValue = obj[propName],
						type = $.type(oldValue);

					if (type === 'array' && self._) {
						oldValue.setRoot && oldValue.setRoot(self._.parent);
						oldValue.setObserve();
					}

					// if obj has this property, so we need to update it in self
					// todo: maybe remove obj.hasOwnProperty(propName)?
					if (Object.prototype.hasOwnProperty.call(obj, propName) && newValue !== undefined) {
						if (type === 'array') {
							if (self._ && self._.newId && oldValue._ && oldValue._.ignoreClone) {

							} else if (oldValue._ && oldValue._.langs) {
								f.initLangsArray(oldValue, newValue);
							} else if (!oldValue._ || !oldValue._.ignoreInit) {
								f.set(oldValue, newValue, false, obj);
							}
						} else if (type === 'object') {
							if (propName === '_') {

							} else if (newValue === null) {
								// if we have { type: 'typeName' } we should create default object of type typeName
								if (oldValue.type && !$.isInitialized(oldValue) && !oldValue.createIfHasValue) {
									f.setProperty(self, propName, f.create({}, oldValue.type, f.setParents ? self._.parent : null));
								}
							} else if (!oldValue.type) {
								// not initialyzable type
								deepExtend(oldValue, newValue);
							} else if (self._ && self._.newId && oldValue._ && oldValue._.ignoreClone) {
								// creating property with default values when cloning and _.ignoreClone = true
								f.setProperty(self, propName, f.create(oldValue.type, f.setParents ? self._.parent : null));
							} else if (!$.isInitialized(oldValue)) {
								// initing
								f.setProperty(self, propName, f.create(newValue, oldValue.type, f.setParents ? self._.parent : null));
							} else if (!oldValue._.ignoreReInit) {
								if (oldValue.unique && oldValue.unique(newValue) === oldValue) {
									// reiniting
									oldValue.init ? oldValue.init(newValue) : f.init(oldValue, newValue);
								} else {
									f.setProperty(self, propName, f.create(newValue, oldValue.type, f.setParents ? self._.parent : null));
								}
							}
						} else {
							if (propName === 'id' && self._ && self._.newId) {
								f.setProperty(self, 'id', -1);
							} else if (propName === 'clientId' && self._ && self._.newId) {
								f.setProperty(self, 'clientId', f.getId());
							} else if (propName === 'deleted') {
								f.setProperty(self, 'deleted', !!newValue);
							} else if (typeof newValue !== 'undefined') {
								f.setProperty(self, propName, newValue);
							}
						}
					} else {
						if (propName === 'deleted') {
							f.setProperty(self, 'deleted', false);
						}
					}
				}
			}

			const objKeys = objectKeys(obj);
			if (objKeys.length > 0) {
				objKeys.forEach(function (item) {
					if (!Object.prototype.hasOwnProperty.call(self, item)) {
						f.setProperty(self, item, obj[item]);
					}
				});
			}
		}

		if (self._ && self._.newId) {
			self._.newId = false;
		}

		if (self.ignoreUnique)
			self.ignoreUnique = false;

		if (self.type && !self._.isClone) {
			const collections = f.getCollections(self);

			if (!collections[self.type]) {
				collections[self.type] = {};
			}

			const items = collections[self.type];

			if (self.id > 0) {
				if (items[self.id]) {
					$.setProperty(items[self.id], self);
					self = items[self.id];
				} else {
					items[self.id] = self;
				}

				if (self.clientId && items[self.clientId]) {
					delete items[self.clientId];
				}
			} else {
				if (self.clientId) {
					if (items[self.clientId]) {
						$.setProperty(items[self.clientId], self);
						self = items[self.clientId];
					} else {
						items[self.clientId] = self;
					}
				}
			}
		}

		return self;
	},
	/** @method
	 * private create object and call init for it
	 * @param {string} type - type of created object
	 * @param {object} proto - prototype of created object
	 * @param {object} values - default properties that extend created object
	 * @param {object} obj - properties that extend created object
	 * @returns {object} - created object
	 */
	_create: function (type, proto, values, obj, parent) {
		const self = this;
		let r = $.extend(true, Object.create(proto), values);

		r.type = type;

		if (self.setParents) {
			let collections = undefined;
			if (!parent) {
				collections = nodejs
					? { xpath: $.collections.xpath, filter: $.collections.filter }
					: $.collections;
			}

			if (!r._) {
				r._ = {
					parent: parent || r,
					collections: collections,
					initialized: true
				};
			} else {
				r._.parent = parent || r._.parent || r;
				r._.collections = collections;
				r._.initialized = true;
			}
		} else {
			if (!r._) {
				r._ = {
					initialized: true
				};
			} else {
				r._.initialized = true;
			}
		}

		if (r.unique && !obj.ignoreUnique) {
			// todo: it would be better if we checked for existing items before creating r with $.extend and Object.create
			r = r.unique(obj) || r;
		}

		if (obj == undefined) {
			obj = type !== 'string' ? type : {};
		}

		if (r.init) {
			r.init(obj);
		} else {
			self.init(r, obj);
		}

		r._.toJSON = function () {
			return null;
		};

		return r;
	},
	/** @method
	 * public create object
	 * @example
	 * $.create({}, 'myType')
	 * @example
	 * $.create('myType')
	 * @param {object} obj - new object will be initialized with this parameter
	 * @param {string} type - type of new object
	 * @returns {object} - created object
	 */
	create: function (obj, type, parent) {
		if ($.type(obj) === 'string') {
			parent = type;
			type = obj;
			obj = {};
		}

		const self = this;

		let newObject;

		if ((!self.protos[type] || self.hasStub(type)) && obj.typePath) {
			if (!self.hasStub(type)) {
				self.addStub(type, 'page');
			}

			pageLoaders.load(obj.typePath).then(function () {
				const pageProto = self.protos[type];
				const pageValues = self.objects[type];

				newObject.ignoreUnique = true;

				self._create(type, pageProto, pageValues, newObject, parent);
			}).catch(function (error) {
				if (nodejs && typeof runtimeGlobal.__legacyWebpackReportSsrError === 'function' &&
					runtimeGlobal.__legacyWebpackReportSsrError(error)) {
					return;
				}

				setTimeout(function () {
					throw error;
				}, 0);
			});
		}

		const proto = self.protos[type] || {};
		const values = self.objects[type];

		newObject = self._create(type, proto, values, obj, parent);

		return newObject;
	},
	/** @method
	 * remove object from collections
	 */
	deleteObject: function (type, id, parent) {
		const collections = $.getCollections(parent, type);

		delete collections[id];
	},
	arrOpt: {
		send: false,
		rows: undefined,
		before: false,
		offset: 0,
		count: 0,
		// if clearBeforeReload is true then array will be cleared in reload method before new items are loaded from server
		// otherwise array will be cleared in set method after new items are loaded from server (no matter if array was loaded with reload, load or lazyload)
		clearBeforeReload: true,
		params: {},
		// able ignore init
		ignoreInit: false,
		// when array with langs-option set to true is inited from the array received from server
		// each element is inited with the element with matching name property ('Ru'-element is inited with 'Ru'-element, 'Eng'-element is inited with 'Eng'-element, disregarding order of elements in arrays)
		// remaining elements from server that doesn't have matches are pushed into array being initialized
		langs: false,
		// when autofill is true array will be filled with undefineds and then elements which loaded from serever will replace undefineds
		// with autofill is true offset is counted from end of array and is negative value
		autofill: false,
		// for compatibility with $.isInitilized
		initialized: true
	},
	// if array is passed as first argument then extend it's options and methods and return it (not creates new array)
	/** @method
	 * creates or extends array
	 * @param {object[]} array - optional paramter. Array to be extended
	 * @param {object} options - options to be extended with $.arrOpt and placed in resulting array _ property
	 * @param {object} methods - additional methods for array
	 * @returns {object[]} - extended array that was passed to method or empty array with and extended options and methods
	 */
	array: function (array, options, methods) {
		const type = $.type(array);

		const a = type === 'array'
			? array
			: [];

		if (type === 'object') {
			methods = options;
			options = array;
		}

		a._ = $.extend({}, this.arrOpt, options);

		if (methods) {
			fastExtend(a, methods);
		}

		return a;
	},
	requestObject: function (p, dontSendNotification, timeout, identifier) {
		// If ajax has been passed to request, then resend it.
		// todo There may be problems with testSession changes after the initial request for the first time has been sent. It won't be updated for the following resend tries
		if (p.xhr) {
			return $.ajax(p);
		}

		if (!timeout) {
			timeout = 10;
		}

		const data = JSON.stringify({
			data: JSON.stringify(p),
			sessionGuid: p.session,
			type: p.type,
			updateExpirationDateTime: p.updateExpirationDateTime,
			connection: dontSendNotification && $.hub ? $.hub.connection.connectionId : null,
			identifier: identifier || null
		});

		return $.ajax({
			type: 'POST',
			contentType: 'application/json',
			url: getRequestPath() + 'api/post/',
			data: data,
			dataType: 'json',
			tries: 0,
			timeout: timeout * 1000
		});
	},
	initJsViewsSetProperty: function () {
		f.setProperty = $.setProperty = function (obj, key, value) {
			$.observable(obj).setProperty(key, value);
		};
	},
	removeFromCollections: function (element) {
		if (element && element.type && element.getCollections) {
			const collections = element.getCollections();
			if (collections) {
				// todo maybe we should find another way to remove object from collections
				delete collections[element.id];
			}
		}
	}
};

$.extend($, f);

f.add('object', {
	_: {
		params: {
			// depth of applying sendArray and sendObject options
			depth: 1,
			// send arrays when save object
			sendArray: false,
			// send inner objects when save object
			sendObject: false
			// ignore init after save request. This options should set in true if we update this kind of object by notifications
			// ignoreInitAfterSave: undefined

			// ignore cloning this object when clone parent object.
			// This options should set in true if we don't need cope some inner options of object
			// exmpale: clone some entity which have comment tree. We need only clone object and get new id after save, but not comments
			// ignoreClone: undefined

			// ignore clear this object if parent object is clearing
			// ignoreClear: undefined

			// create object if base object r has value for this property
			// createIfHasValue: undefined

			// this is hidden option, using in clone process, to reset id in -1 and get new client id
			// newId: undefined
		},
		// this is hidden option showing request status
		send: false

		// if don't need to check collections for existing objects
		// ignoreUnique: undefined

		// if dontSend is true, object won't be sent to server
		// dontSend: undefined
	},
	id: -1,
	deleted: false
}, {
	clearInNotification: true,
	// cts: { 'client_name': 'server_name' },
	// stc: { 'server_name_to_lower_case': 'client_name' },
	success: function (r) {
		console.log(r);
	},
	err: function (r) {
		console.log(r);
		// switch (r.code) {
		//	case 1:
		//		site.loginDialog();
		//		break;
		// }
	},
	init: function (r) {
		this.clientId = f.getId();
		if (r && r.data) {
			this.id = r.data.id;
		}
		f.init(this, r);
	},
	unique: function (obj) {
		if (nodejs)
			return null;

		const collections = f.getCollections(this, this.type);
		return collections && collections[obj.id];
	},
	handle: function (r) {
		console.log(r);
	},
	requestEnding: function (slowRequests) {
		const self = this;

		f.setProperty(self._, 'send', false);
		clearTimeout(self._.timerId);
		slowRequests.removeRange(slowRequests.indexOf(self._.timerId), 1);
	},
	request: function (params, success, err, timeout, requestOptions) {
		const site = $.site,
			self = this;

		if (!requestOptions) {
			requestOptions = {
				allowMultipleRequests: false,
				useLatest: false,
			};
		}

		if (!site.banned) {
			if (!self._.send || self._.abortAjax || requestOptions.allowMultipleRequests) {
				if (self._.abortAjax && self._.ajax) {
					self._.ajax.abort();
				}

				if (!params.xhr) {
					self._.params.type = params.type;
				}

				const f = $,
					callbacks = {
						success: success,
						err: err,
						timeout: timeout
					};

				f.setProperty(self._, {
					send: true,
					timerId: setTimeout(function () {
						site.slowRequests._push(self._.timerId);
					}, site.slowRequestTimeout)
				});

				if (!params.random) {
					const value = Math.random();
					self._.params.random = value;
					params.random = value;
				}

				let identifier = null;
				if (!params.xhr) {
					identifier = Math.random().toString(36).substring(2);
					params.session = cookies.get(typeof site.getCookieSessionName === 'function' ? site.getCookieSessionName() : 'session');
					params.type = f.cts[params.type] || params.type;
					self._.identifier = identifier;
				}

				const successfulCallback = function (d) {
					const response = d.response;

					if (response.additional) {
						console.log(response);
					}

					if (d.session && site.session && d.session !== site.session && params.type !== 'logoff') {
						site.setSession(d.session);

						site.connectionStopped = true;

						$.hub && $.hub.stop();
					}

					if (d.buildNumber && site.buildNumber && d.buildNumber !== site.buildNumber) {
						f.setProperty(site, 'newReleaseAvailable', true);
					}

					f.setProperty(self._, 'loaded', true);

					if (d.success && !response.deleted && !params.ignoreInitAfterSave) {
						if (self._.params && self._.params.random) {
							self._.params.random = null;
						}

						if (requestOptions.useLatest) {
							if (d.identifier === self._.identifier) {
								self.init(response);
							}
						} else {
							self.init(response);
						}
					}

					if (!d.success && response.code) {
						if (!site.captchaRequests) {
							f.setProperty(site, 'captchaRequests', []);
						}

						let captchaRequest;
						switch (response.code) {
							case 1:
								site.loginDialog(d);
								break;
							case 65:
								site.handleNotAvailableError(response);
								break;
							case 66:
								self.requestFail && self.requestFail(response.code);

								captchaRequest = response;
								f.setProperty(captchaRequest, 'callbacks', callbacks);
								f.setProperty(captchaRequest, 'caller', self);
								site.captchaRequests._push(captchaRequest);
								site.captchaDialog();
								break;
							case 67:
								self.requestFail && self.requestFail(response.code);

								captchaRequest = response;
								f.setProperty(captchaRequest, 'callbacks', callbacks);
								f.setProperty(captchaRequest, 'caller', self);
								site.captchaRequests._push(captchaRequest);
								site.banDialog();
								break;
							default:
								if (site.onErrorCallback) {
									site.onErrorCallback(d, response);
								}
								break;
						}
					}

					f.setProperty(site, 'time', d.time);

					const c = d.success ? 'success' : 'err';

					if (this && this.tries > 0) {
						f.setProperty(site, 'failedRequests', (site.failedRequests || 1) - 1);
					}

					let isLatest = true;

					if (requestOptions.useLatest && d.identifier !== self._.identifier) {
						isLatest = false;
					}

					self.requestEnding(site.slowRequests);

					if (isLatest) {
						(callbacks[c] ? callbacks[c] : self[c]).call(self, d.response);
					}
				};

				const makeRegularRequest = function () {
					self._.ajax = f.requestObject(params, self._.dontSendNotification, site.requestTimeout, identifier)
						.done(successfulCallback)
						.fail(function (err) {
							self.requestEnding(site.slowRequests);

							console.log(err);

							self.requestFail && self.requestFail();

							// Different browsers can return either 'cancelled' or 'canceled'. Seems legit.
							if (!site.debug && (err.statusText === 'timeout' || err.statusText === 'cancelled' || err.statusText === 'canceled')) {
								if (this.tries < 1) {
									if (this.tries === 0) {
										site.failedRequests++;

										if (callbacks.timeout) {
											callbacks.timeout(true, err);
										} else {
											site.timeoutDialog(err);
										}
									}
									this.tries++;
									// self - initial data, this - ajax object.
									// Passing the ajax object to get it resended one more time
									site.request.call(self, this, callbacks.success, callbacks.err, callbacks.timeout);
								} else {
									if (callbacks.timeout) {
										callbacks.timeout(false, err);
									}
								}
							}
						});
				};

				if (params.useSignalr) {
					const request = {
						data: JSON.stringify(params),
						sessionGuid: params.session,
						updateExpirationDateTime: params.updateExpirationDateTime,
						type: params.type,
					};

					if ($.hub && $.hub.invoke && $.hub.state === 'Connected') {
						$.hub.invoke('handle', request).then(successfulCallback, makeRegularRequest);
					} else {
						makeRegularRequest();
					}
				} else {
					makeRegularRequest();
				}
			} else {
				console.log('this object waiting answer...', self);
			}
		}
		return self;
	},
	/** @method
	 * modifies arguments of load or save methods:
	 * if first argument is a number then replace it with self._.params extended with { count : <first_argument> }
	 * if first argument is an object then replace it with self._.params extended with first argument
	 * otherwise unshift self._.params as first argument
	 * besides that if second and third arguments are false-like values then replace them with undefined
	 * @example
	 * my_array.pse([1, success_callback, err_callback])
	 * @example
	 * my_object.pse([{ withItems: true }, success_callback])
	 * @example
	 * my_object.pse([success_callback, err_callback])
	 * @param {object[]} arr - arguments object of load or save methods
	 * @returns {object[]} - array of modified arguments; first element of array is extended self._.params
	 */
	pse: function (arr) {
		const self = this,
			arg = arrayPrototype.slice.call(arr, 0);
		if (arg.length > 0) {
			const type = $.type(arg[0]);
			if (type === 'number') {
				arg[0] = $.extend({}, self._.params, {
					count: arg[0]
				});
			} else if (type === 'function') {
				arg.unshift(self._.params);
			} else {
				arg[0] = $.extend({}, self._.params, arg[0]);
			}

			arg[1] = arg[1] ? arg[1] : undefined;
			arg[2] = arg[2] ? arg[2] : undefined;
			arg[3] = arg[3] ? arg[3] : undefined;
		} else {
			arg.push(self._.params);
		}

		return arg;
	},
	// 1 param - count of load items or object with load params
	// 2 param - success callback
	// 3 param - error callback
	// 4 param - timeout callback
	load: function () {
		const self = this;
		$.setProperty(self, 'requestType', 'load');
		return self.request.apply(self, self.pse(arguments));
	},
	lazyload: function (params, callback) {
		if (this._.loaded) {
			if ($.type(params) === 'function') {
				callback = params;
			}
			callback && callback();
		} else {
			this.load(params, callback);
		}
	},
	reload: function () {
		const self = this;
		if (self._.clearBeforeReload) {
			self.clear();
		}
		self.load.apply(self, arguments);
	},
	reloadWithDelay: function (delay, callback) {
		if (typeof delay == 'undefined')
			delay = 300;

		const self = this;

		clearTimeout(self._.timeout);

		self._.timeout = setTimeout(function () {
			self.reload(callback);
		}.bind(self), delay);
	},
	/** @method
	 * gets data to be sent to server by transforming object
	 * uses _.params.depth, _.params.sendArray, _.params.sendObject of object and _.dontSend of object's properties
	 * if _.params.sendArray is false-like value, arrays are ignored
	 * if _.params.sendObject is false-like value, objects are ignored
	 * object properties with true-like _.dontSend are ignored
	 * if depth value is exceeded then arrays are replaced with [] and objects are replaced with {}
	 * if object properties which doesn't have form method (not derived from 'object' type) are added to request data then there will be no further checks of depth, sendArray, sendObject and dontSend for inner objects of this property
	 * @param {object} params - _.params of object extended by pse method
	 * @returns {object} - object transformed for sending to server
	 */
	form: function (params) {
		const self = this,
			obj = {};
		objectKeys(self).forEach(function (item) {
			if (item !== '_' && item !== 'onHandle') {
				const prop = self[item];
				if (!(prop && prop._ && prop._.dontSend)) {
					if (prop instanceof Array) {
						if (params.sendArray) {
							obj[item] = [];
							if (params.depth > 0) {
								const p = $.extend({}, params, {
									depth: params.depth - 1
								});
								prop.forEach(function (a) {
									obj[item].push(a && a.form ? a.form(p) : a);
								});
							}
						}
					} else if (prop instanceof Object) {
						if (params.sendObject) {
							obj[item] = prop.form
								? params.depth > 0
									? prop.form($.extend({}, params, { depth: params.depth - 1 }))
									: {}
								: prop;
						}
					} else {
						obj[item] = prop;
					}
				}
			}
		});
		return obj;
	},
	// 1 param - save params
	// 2 param - success callback
	// 3 param - error callback
	// 4 param - timeout callback
	save: function () {
		const self = this,
			arg = self.pse(arguments);
		$.setProperty(self, 'requestType', 'save');

		arg[0] = self.form(arg[0]);
		arg[0].ignoreInitAfterSave = typeof arg[0].ignoreInitAfterSave !== 'undefined' ? arg[0].ignoreInitAfterSave : self._.ignoreInitAfterSave;
		return self.request.apply(self, arg);
	},
	remove: function (params, success, error) {
		if (this.id > 0) {
			this.deleted = true;
			this.save(params, success, error);
		}
	},
	template: function () {

	},
	xpath: function (path) {
		return $.xpath(this, path);
	},
	_clone: function () {
		const self = this;
		self._.newId = self.ignoreUnique = true;
		const r = self.createObject(self, self.type);
		self.ignoreUnique = self._.newId = false;
		r._.isClone = true;
		return r;
	},
	clone: function () {
		return this._clone();
	},
	_deepClone: function (
		o													// The only argument passed by user: object to clone
		, h													// Cache (organized as array: [key,value,key,value,...])
	) {
		let i, r, x;
		const // Property indexer, result, temporary variable
			t = [Array, Date, Number, String, Boolean],	// Types to treat in a special way
			s = Object.prototype.toString;				// Shortcut to Object.prototype.toString
		h = h || [];										// If cache is not created yet, create it!
		for (i = 0; i < h.length; i += 2) {					// Search cache for our object
			if (o === h[i]) {
				r = h[i + 1];
			}
		}

		if (!r && o && typeof o == 'object') {				// Clone o if it is uncached object and not null
			r = {};											// Default result template: plain hash

			if (o._) {
				o.ignoreUnique = true;
			}

			for (i = 0; i < t.length; i++) {				// To handle multiframe environment, search for type by
				if (s.call(o) == s.call(					//   comparing Object.prototype.toString's of our object
					x = new t[i](o)
				)) {						//   and new object x created with the constructor t[i]
					// Notice that it will create new Date(o), new String(o)
					//   which is good and new Array(o) which is bad
					r = i ? x : [];							// If i==0, t==Array. We need to recreate it. Else use x
				}
			}

			h.push(o, r);									// Add object to cache before (!) making recursive call
			for (i in o) {									// Just copy properties recoursively
				if (h.hasOwnProperty.call(o, i) &&             // As o might have key 'hasOwnProperty', use something
					(!i.startsWith || (!i.startsWith('jQuery') && i !== 'onHandle'))) { // We should never copy jQuery observers
					r[i] = this._deepClone(o[i], h);		//   we defined right instead
					if (i == '_') {
						r[i].isClone = true;
						r[i].ignoreReInit = true;
						if (o._.parent) {
							r[i].parent = o._.parent;
						}
					}
				}
			}

			if (o._) {
				o.ignoreUnique = false;
			}
		}
		return r || o;										// Return r if it was found in cache or built in if(){}
	},
	deepClone: function () {
		const self = this,
			copy = self._deepClone(self);
		copy.ignoreUnique = copy._.isClone = copy._.ignoreReInit = true;
		return self.createObject(copy, copy.type);
	},
	_clear: function (depth) {
		const self = this;
		if (depth == undefined) depth = 3;
		else if (depth === 0) return;
		for (let property in self) {
			if (Object.prototype.hasOwnProperty.call(this, property)) {
				const obj = self[property];

				switch ($.type(obj)) {
					case 'object':
						if (property !== '_' && property !== 'parentNode') {
							if ($.isInitialized(obj) && obj._clear && !obj._.ignoreClear) {
								obj._clear(depth - 1);

								// todo: empty object is currently created without properties that was initially in _ like ignoreClone
								f.setProperty(self, property, obj.type && !obj._.setNullInsteadEmptyObject ? { type: obj.type } : null);
							}
						}
						break;

					case 'array':
						if (obj._ && obj._.params && !obj._.params.ignoreClear) obj.clear(true, depth - 1);
						break;

					default:
						// #todo maybe we should do it here...
						// delete self[property];
						break;
				}
			}
		}

		const items = f.getCollections(self, self.type);

		if (items) {
			delete items[self.id > 0 && !self._.isClone ? self.id : self.clientId];
		}
	},
	clear: function (depth) {
		this._clear(depth);
	},
	saveProperty: function (field, success, error) {
		$.create({
			_: {
				params: { sendArray: true }
			},
			type: 'property',
			objectType: this.type,
			field: field,
			value: this[field],
			id: this.id
		}, 'object').save(success, error);
	},
	/** @method
	 * get root object or undefined if object contains no root link
	 * @returns {object}
	 */
	getRoot: function () {
		return $.setParents
			? this._.parent
			: $.site;
	},
	/** @method
	 * create object with root link
	 * @param {object} obj - default values
	 * @param {string} type - type of new object
	 * @returns {object} - new object
	 */
	createObject: function (obj, type) {
		return $.create(obj, type, $.setParents ? this._.parent : null);
	},
	// if array is passed as first argument then extend it's options and methods and return it (not creates new array)
	/** @method
	 * creates or extends array and sets root link
	 * @param {object[]} array - optional paramter. Array to be extended
	 * @param {object} options - options to be extended with $.arrOpt and placed in resulting array _ property
	 * @param {object} methods - additional methods for array
	 * @returns {object[]} - extended array that was passed to method or empty array with and extended options and methods
	 */
	createArray: function (array, options, methods) {
		const result = $.array(array, options, methods);

		result._.parent = this._.parent;

		return result;
	},
	/** @method
	 * Get collections of objects of this object's type.
	 * If $.setParents flag is true then returns collections contained in root object
	 * else return $.collections[this.type]
	 * @returns {object}
	 */
	getCollections: function () {
		return $.getCollections(this, this.type);
	},
	/** @method
	 * get element of the same type with specified id from collections
	 * @param {number} id
	 * @returns {object}
	 */
	cget: function (id) {
		return $.cget(this, this.type, id);
	},
	/** @method
	 * get element of the same type with specified value of specified property from collections
	 * or array of such elemetns if 'all' parameter is true
	 * @param {string} property
	 * @param {string|number|object|boolean} value
	 * @param {boolean} all
	 * @returns {object|object[]}
	 */
	cxpath: function (property, value, all) {
		return $.cxpath(this, this.type, property, value, all);
	},
	views: {
		keys: {
			id: {
				show: false
			},
			clientId: {
				show: false
			},
			_type: {
				show: false
			},
			deleted: {
				show: false
			}
		}
	}
});

const objectProto = f.protos.object;

fastExtend(arrayPrototype, {
	success: objectProto.success,
	err: objectProto.err,
	requestEnding: objectProto.requestEnding,
	request: objectProto.request,
	pse: objectProto.pse,
	reload: objectProto.reload,
	reloadWithDelay: objectProto.reloadWithDelay,
	// push - if true (default value) push all items, otherwise reinit items with matching ids and push everything else
	init: function (r, push) {
		// todo: maybe push should be false by default? push is false in $.init for array-properties of objects
		$.set(this, r, push === undefined ? true : push);
	},
	setObserve: function () {
		const self = this;
		if (self._ && self._.observe && !nodejs && !self._.observed) {
			self._.observed = true;

			$.observe(self, function (ev, arg) {
				const array = ev.target;

				if (array._.ignoreObserving || arg.change === 'move' || arg.change === 'refresh')
					return;

				if (array._ && typeof array._.rows !== 'undefined') {
					f.setProperty(array._, 'rows', array._.rows + (arg.change === 'insert' ? arg.items.length : -arg.items.length));
				}
			});
		}
	},
	insert: function (i, item, isWaitedLoad) {
		return this.insertRange(i, item, isWaitedLoad);
	},
	_push: function (item, isWaitedLoad) {
		return this.insert(this.length, item, isWaitedLoad);
	},
	// when callback is passed to this method
	// not only it is called after removal of element from array
	// but also request for deletion is sent to server
	// todo: maybe this method should be changed
	remove: function (i, s, e) {
		const self = this;
		if (s != undefined) {
			if (i > -1) {
				const t = self[i];
				if (t.id > -1) {
					self[i].remove(function (r) {
						if (self[i] === t) {
							self.removeRange(i, 1);
						}

						s(r);
					}, e);
				} else {
					self.removeRange(i, 1);
					s();
				}
			}
		} else {
			self.removeRange(i, 1);
		}

		return self;
	},
	_pop: function () {
		return this.remove(this.length - 1);
	},
	clone: function () {
		const t = $.extend(true, this.slice(), this);
		t.init();
		return t;
	},
	clear: function (clearEachItem, depth) {
		const self = this;
		if (depth == undefined) depth = 3;
		if (self._) {
			f.setProperty(self._, {
				offset: 0,
				rows: undefined
			});
		}
		if (clearEachItem) {
			self.forEach(function (item) {
				if (item && item._clear) item._clear(depth);
			});
		}
		return self.removeRange(0, self.length);
	},
	_clear: function (clearEachItem) {
		const self = this;
		if (self._) {
			f.setProperty(self._, {
				offset: 0,
				rows: undefined
			});
		}
		if (clearEachItem) {
			self.forEach(function (item) {
				if (item && item.clear) item.clear();
			});
		}
		return self._removeRange(0, self.length);
	},
	_filter: function (ids, have, func) {
		const self = this,
			a = [],
			f = func === undefined ? function (a, b) {
				return a.id === b;
			} : func;
		for (let i = 0; i < self.length; i++) {
			let t = true;
			for (let j = 0; j < ids.length; j++) {
				if (f(self[i], ids[j])) {
					if (have) {
						a.push(self[i]);
					}
					t = false;
					break;
				}
			}
			if (t && !have) {
				a.push(self[i]);
			}
		}
		return a;
	},
	shuffle: function (b) {
		const self = this;
		let i = self.length,
			j, t;

		while (i) {
			j = Math.floor(i-- * Math.random());
			t = b && typeof self[i].shuffle !== 'undefined' ? self[i].shuffle() : self[i];
			self[i] = self[j];
			self[j] = t;
		}

		return self;
	},
	_shuffle: function (b) {
		arrayPrototype.shuffle.call(this, b);
		return this.refresh();
	},
	load: function () {
		const self = this,
			p = self._,
			arg = self.pse(arguments);

		if (!p.clearBeforeReload) {
			arg[0].offset = arg[0].offset || 0;
		} else {
			if (arg[0].count > 0) {
				arg[0].offset = p.offset + self.length;
			} else {
				p.negativeCount = true;
				arg[0].count = -arg[0].count;
				arg[0].offset = p.offset - arg[0].count;
			}
		}

		if (self.length === 0 || self.length + p.offset < p.rows || p.autofill || typeof p.rows === 'undefined' || !p.clearBeforeReload) {
			self.request.apply(self, arg);
		}

		self.requestType = 'load';

		return self;
	},
	lazyload: function (params, callback) {
		const self = this,
			length = self.length;

		if (typeof self._.rows === 'undefined') {
			if (typeof params === 'number' && params > length && length > 0)
				params = params - length;

			self.load(params, callback);
		} else {
			if ($.type(params) === 'function')
				callback = params;

			if (callback)
				callback(self);
		}
	},
	equal: function (a, b, prop) {
		return a[prop] === b;
	},
	get: function (prop, value, index) {
		const self = this;
		let r = index ? -1 : null,
			func = self.equal;
		if (prop !== undefined) {
			if ($.type(prop) === 'function') {
				func = prop;
			} else {
				if (value === undefined) {
					value = prop;
					prop = 'id';
				}
			}

			const l = self.length;
			for (let i = 0; i < l; ++i) {
				const element = self[i];
				if (typeof element !== 'undefined' && func(element, value, prop)) {
					r = index ? i : self[i];
					break;
				}
			}
		}
		return r;
	},
	search: function (prop, value) {
		return this.get(prop, value, true);
	},
	xpath: function (path, value, index) {
		return this.get(function (a, b) {
			return $.xpath(a, path) === b;
		}, value, index);
	},
	__remove: function (valueOrIdOrPredicate) {
		const self = this;
		if (typeof valueOrIdOrPredicate === 'function') {
			const predicate = valueOrIdOrPredicate;
			const observableThis = $.observable(self);
			for (let i = 0; i < self.length; ++i) {
				if (self[i] && predicate(self[i])) {
					observableThis.remove(i);
					--i;
				}
			}
		} else if (self._) {
			const id = valueOrIdOrPredicate;
			return self.__remove(function (item) {
				return item.id === id;
			});
		} else {
			const value = valueOrIdOrPredicate;
			return self.__remove(function (item) {
				return item === value;
			});
		}

		return self;
	},
	createObject: function (obj, type) {
		return $.create(obj, type, $.setParents ? this._.parent : null);
	},
	/**
	 * set root for array
	 * @param {object} obj - any object with root link
	 */
	setRoot: function (obj) {
		const self = this;

		if ($.setParents) {
			if (!self._) {
				self._ = { parent: $.getRoot(obj) };
			} else {
				self._.parent = $.getRoot(obj);
			}
		}
	}
});

f.add('tree', {
	_: {
		fields: {
			children: 'children',
			parent: 'id',
			child: 'parent'
		}
	}
}, 'object', {
	_remove: objectProto.remove,

	get: function (prop, value) {
		const self = this;
		if (prop !== undefined) {
			if (value === undefined) {
				value = prop;
				prop = 'id';
			}

			if (self[prop] === value) {
				return self;
			} else {
				let t;
				for (let i = 0; i < self[self._.fields.children].length; i++) {
					t = self[self._.fields.children][i].get(prop, value);
					if (t != null) {
						if (!t._.parent) {
							t._.parent = self;
							t._.index = i;
						}
						return t;
					}
				}
			}
		}
		return null;
	},
	func: function (func) {
		const self = this;
		if (func(self)) {
			return self;
		} else {
			let t;
			for (let i = 0; i < self[self._.fields.children].length; i++) {
				t = self[self._.fields.children][i].func(func);
				if (t != null) {
					if (!t._.parent) {
						t._.parent = self;
						t._.index = i;
					}
					return t;
				}
			}
		}
		return null;
	},
	xpath: function (path, value) {
		const self = this,
			arr = path.split('.'),
			f = arr.length > 1 ? function (node) {
				let r = node;
				arr.forEach(function (item) {
					r = r[item];
				});
				return r === value;
			} : function (node) {
				return node[path] === value;
			};

		if (f(self)) {
			return self;
		} else {
			for (let i = 0; i < self[self._.fields.children].length; i++) {
				const t = self[self._.fields.children][i].func(f);
				if (t != null) {
					if (!t._.parent) {
						t._.parent = self;
						t._.index = i;
					}
					return t;
				}
			}
		}
		return null;
	},
	activate: function (node) {
		const self = this;
		$.setProperty(self.current, 'active', false);
		$.setProperty(self, 'current', node || self);
		$.setProperty(self.current, 'active', true);
	},
	remove: function (index, f, e) {
		const self = this;

		if (index instanceof Function) {
			e = f;
			f = index;
		}

		if (index instanceof Function && self._ && self._.parent && self._.index > -1) {
			if (!self._.removed) {
				self._.removed = true;
				const children = self._.parent[self._.fields.children];
				if (children.length > self._.index) {
					children.remove(self._.index, function (r) {
						for (let i = self._.index; i < children.length; ++i) {
							children[i]._.index--;
						}

						f && f(r);
					}.bind(self), e);
				}
			} else {
				if (f) {
					self._remove(f, e);
				}
			}
		} else {
			if (!self._.removed) {
				self[self._.fields.children][index]._.removed = true;
				self[self._.fields.children].remove(index, f, e);
			} else {
				self._remove(f, e);
			}
		}
	},
	_push: function (node) {
		const self = this;

		node._.parent = self;
		node._.index = self[self._.fields.children].length;

		self[self._.fields.children]._push(node);
	},
	push: function (node, anyway) {
		const self = this;
		if (self._ && self._.fields && self._.fields.parent && self._.fields.child) {
			const t = self.xpath(self._.fields.parent, $.xpath(node, self._.fields.child));

			const arr = (t ? t : self)[self._.fields.children];

			if (t || anyway) {
				arr._push(arr._ && arr._.type && !node.type ? $.create(node, arr._.type) : node);
			}
		}
	},
	getCount: function () {
		const self = this;
		let c = self[self._.fields.children].length;
		self[self._.fields.children].forEach(function (item) {
			c += item.count();
		});
		return c;
	},
	getParent: function () {
		const self = this;
		let r = null;
		if (self._ && self._.fields && self._.fields.parent) {
			r = self.get(self[self._.fields.parent]);
		} else {
			console.log('can\'t get parent');
		}
		return r;
	}
});

export default $;
